/**
 * Changes tab: every Pax8 licence change request with its stage ledger (scope §0 "Pax8 integration plan"): the
 * quantity PUT, the price hold, the customer note and billing. A change Pax8 may or may not have taken
 * (`unconfirmed`) or that needs a person (`partial`) is shown first: those are the ones to check by hand.
 */
import { Chip, type ChipTone, EmptyState } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRight, Check, CircleDashed, Clock, Loader, Minus, Pause, TriangleAlert, X } from 'lucide-react'
import * as React from 'react'
import type { ChangeState, LicenceChangeOut, StageState } from '../../lib/api/generated'
import { timeLabel } from '../profit/format'
import { QueryView } from '../profit/states'
import { cad, changesQuery, FilterChips, pax8Url } from './common'

export const STATE_META: Record<ChangeState, { label: string; tone: ChipTone; rank: number }> = {
  unconfirmed: { label: 'Check by hand', tone: 'danger', rank: 0 },
  failed: { label: 'Failed', tone: 'danger', rank: 1 },
  partial: { label: 'Needs a person', tone: 'warning', rank: 2 },
  waiting_provisioning: { label: 'Waiting on Pax8', tone: 'warning', rank: 3 },
  running: { label: 'Running', tone: 'primary', rank: 4 },
  claimed: { label: 'Starting', tone: 'primary', rank: 5 },
  done: { label: 'Done', tone: 'success', rank: 6 },
}

const STAGES: { key: keyof LicenceChangeOut['stages']; label: string }[] = [
  { key: 'pax8', label: 'Pax8 quantity' },
  { key: 'price', label: 'Price hold' },
  { key: 'notice', label: 'Customer note' },
  { key: 'billing', label: 'Billing' },
]

const STAGE_META: Record<StageState, { label: string; className: string; icon: React.ReactNode }> = {
  pending: { label: 'pending', className: 'text-muted-foreground', icon: <CircleDashed aria-hidden /> },
  running: { label: 'running', className: 'text-primary', icon: <Loader aria-hidden /> },
  done: { label: 'done', className: 'text-success', icon: <Check aria-hidden /> },
  skipped: { label: 'skipped', className: 'text-muted-foreground', icon: <Minus aria-hidden /> },
  held: { label: 'held as a draft', className: 'text-warning', icon: <Pause aria-hidden /> },
  failed: { label: 'failed', className: 'text-destructive', icon: <X aria-hidden /> },
  unconfirmed: { label: 'unconfirmed', className: 'text-destructive', icon: <TriangleAlert aria-hidden /> },
}

export function StageLedger({ change }: { change: LicenceChangeOut }) {
  return (
    <ol aria-label="Stage ledger" className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
      {STAGES.map((s, i) => {
        const state = change.stages[s.key]
        const meta = STAGE_META[state]
        return (
          <li key={s.key} className="flex items-center gap-1.5" data-stage={s.key} data-state={state}>
            {i > 0 ? <ArrowRight aria-hidden className="size-3 text-subtle" /> : null}
            <span
              className={`inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 [&_svg]:size-3 ${meta.className}`}
            >
              {meta.icon}
              <span className="text-foreground">{s.label}</span>
              <span>{meta.label}</span>
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function ReadBack({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && typeof v !== 'object')
  if (entries.length === 0) return null
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="font-mono">{String(v)}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

function ChangeRow({ change }: { change: LicenceChangeOut }) {
  const meta = STATE_META[change.state]
  const [open, setOpen] = React.useState(false)
  const delta = change.quantity_before == null ? null : change.quantity_after - change.quantity_before
  return (
    <li className="space-y-2 rounded-lg border border-border bg-card px-4 py-3" data-testid="licence-change" data-state={change.state}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">
            {change.product_name ?? change.sku ?? 'Licence'}{' '}
            <span className="tabular font-mono text-sm">
              {change.kind === 'new_subscription'
                ? `new · ${change.quantity_after}`
                : `${change.quantity_before ?? '?'} → ${change.quantity_after}`}
            </span>
            {delta ? <span className="ml-1 text-sm text-muted-foreground">({delta > 0 ? `+${delta}` : delta})</span> : null}
          </p>
          <p className="text-xs text-muted-foreground">
            {timeLabel(change.created_at)}
            {change.confirmed_by ? ` · confirmed by ${change.confirmed_by.name}` : ''}
            {change.price_before != null ? ` · price held at ${cad(change.price_before)}` : ''}
            {change.completed_at ? ` · finished ${timeLabel(change.completed_at)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {change.next_poll_at && change.state === 'waiting_provisioning' ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground [&_svg]:size-3">
              <Clock aria-hidden /> next check {timeLabel(change.next_poll_at)}
            </span>
          ) : null}
          <Chip tone={meta.tone}>{meta.label}</Chip>
        </div>
      </div>
      <StageLedger change={change} />
      {change.last_error ? <p className="text-xs text-destructive">Last error: {change.last_error}</p> : null}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {change.case_id ? (
          <Link to="/case/$id" params={{ id: change.case_id }} className="text-primary underline-offset-2 hover:underline">
            Open the case
          </Link>
        ) : null}
        <a
          href={pax8Url(change.pax8_company_id) ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-2 hover:underline"
        >
          Pax8 subscriptions
        </a>
        <span className="text-muted-foreground">attempts {change.attempts ?? 0}</span>
        {change.read_back ? (
          <button
            type="button"
            className="cursor-pointer text-primary hover:underline"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? 'Hide' : 'Show'} Pax8 read-back
          </button>
        ) : null}
      </div>
      {open && change.read_back ? <ReadBack data={change.read_back} /> : null}
    </li>
  )
}

type View = 'attention' | 'running' | 'done' | 'all'
const VIEW_TEST: Record<View, (c: LicenceChangeOut) => boolean> = {
  attention: (c) => ['unconfirmed', 'failed', 'partial'].includes(c.state),
  running: (c) => ['claimed', 'running', 'waiting_provisioning'].includes(c.state),
  done: (c) => c.state === 'done',
  all: () => true,
}

export function ChangesTab() {
  const q = useQuery(changesQuery())
  const [view, setView] = React.useState<View>('all')
  return (
    <QueryView query={q} what="Licence changes">
      {(data) => {
        const changes = data.changes ?? []
        const rows = changes
          .filter(VIEW_TEST[view])
          .sort((a, b) => STATE_META[a.state].rank - STATE_META[b.state].rank || b.created_at.localeCompare(a.created_at))
        return (
          <div className="space-y-3.5">
            <FilterChips
              label="Change state"
              value={view}
              onChange={setView}
              options={[
                { id: 'all', label: 'All', count: changes.length },
                { id: 'attention', label: 'Needs attention', count: changes.filter(VIEW_TEST.attention).length },
                { id: 'running', label: 'In progress', count: changes.filter(VIEW_TEST.running).length },
                { id: 'done', label: 'Done', count: changes.filter(VIEW_TEST.done).length },
              ]}
            />
            {rows.length === 0 ? (
              <EmptyState
                title={changes.length === 0 ? 'No licence change has been requested yet.' : 'No change in this view.'}
                detail={changes.length === 0 ? 'Changes start from the Licences section on a case.' : undefined}
              />
            ) : (
              <ul aria-label="Licence changes" className="space-y-2">
                {rows.map((c) => (
                  <ChangeRow key={c.id} change={c} />
                ))}
              </ul>
            )}
          </div>
        )
      }}
    </QueryView>
  )
}
